import { useCallback, useEffect, useState } from "react";
import { useWorkflowsStore } from "../stores/workflows";
import { useEleventEventStore } from "../stores/elementEvent";
import {
  extractElementProperties,
  generateEventHandlerPrompt,
  getAllFormElements,
} from "../utils";

type TabType = 'chat' | 'design' | 'workflow' | null;

/**
 * Custom hook to handle cross-frame communication
 */
export function useIframeMessaging() {
  const [activeTab, setActiveTab] = useState<TabType>(null);
  const [shouldEnableInspect, setShouldEnableInspect] = useState<boolean>(false);
  
  // Effect to listen for messages from the parent window
  useEffect(() => {    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'TAB_CHANGED') {
        const { activeTab: newActiveTab } = event.data.payload || {};
        
        setActiveTab(newActiveTab as TabType);
        
        // Disable inspect mode for chat tab
        if (newActiveTab === 'chat') {
          setShouldEnableInspect(false);
        }
        // Enable inspect mode for design and workflow tabs
        else if (newActiveTab === 'design' || newActiveTab === 'workflow') {
          setShouldEnableInspect(true);
        }
      } else if (event.data?.type === 'WORKFLOW_LIST') {
        useWorkflowsStore.getState().setWorkflows(event.data.workflows);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };  }, [activeTab]);

  // Send selected elements to parent window
  const sendSelectedElements = useCallback((elements: HTMLElement[]) => {
    window.parent.postMessage({
      type: 'ELEMENT_INSPECTOR_SELECTED',
      payload: {
        referenceInputs: getAllFormElements(),
        elements: elements.map(el => extractElementProperties(el))
      }
    }, '*');
  }, []);
  
  // Send generated prompt to parent window
  const sendPrompt = useCallback((prompt: string, elements: HTMLElement[]) => {
    let finalPrompt = prompt
    const workflowId = useWorkflowsStore.getState().workflowId
    if (!!workflowId && !!prompt) {
      const { selectedEvent, selectedAction } = useEleventEventStore.getState();
      finalPrompt += generateEventHandlerPrompt({
        event: selectedEvent,
        action: selectedAction,
        elements
      })
    }
    
    window.parent.postMessage({
      type: 'ELEMENT_INSPECTOR_PROMPT',
      payload: {
        workflowId,
        prompt: finalPrompt,
        activeTab,
        referenceInputs: getAllFormElements(),
        elements: elements.map(el => extractElementProperties(el))
      }
    }, '*');
  }, [activeTab]);
  
  return {
    sendSelectedElements,
    sendPrompt,
    activeTab,
    shouldEnableInspect
  };
}
